import styled from '@emotion/styled';
import StarIcon from '@icon/2StarIcon';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import CheckContained from 'aquilla/icons/misc/CheckContained';
import React, { useEffect } from 'react';
import { getImage } from '@utils/imageHelper';
import Discount from 'aquilla/icons/subscriptions/Discount';
import Image from '@base/FutureImage';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { segment } from '@actions/analytics';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import segmentStorage from '@events/segmentStorage';
import useExperiments from '@stores/experiments';
import { getExperimentVariation } from '@stores/selectors/experiments';
import { abVariations } from '@utils/abHelper';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsFreeUser } from '@api/selectors/auth';
import { useDispatch } from 'react-redux';
import useFreeTrial from '@cont/NewPreSubscribed/FreeTrial/useFreeTrial';

const SecContainer = styled.div`
  position: relative;
  width: 1040px;
  margin: auto;
  margin-top: 34px;
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoTablet} {
    width: 100%;
    margin-bottom: 0;
    padding: ${Spacings.SPACING_4B};
    background-color: var(--color-i-violet-dark-1);
    border-radius: ${Spacings.SPACING_2B};
  }
`;

const Arrow = styled.div`
  position: absolute;
  right: 171px;
  width: 33.24px;
  height: 34px;
  border-radius: ${Spacings.SPACING_1B};
  background-color: var(--color-i-violet-dark-1);
  transform-origin: top left;
  transform: skewX(2.33deg) rotate(-45deg);
  border: 1px solid #e9eef2;
  border-left: none;
  border-bottom: none;
  ${mediaQuery.uptoMobile} {
    left: 50%;
    transform: translateX(-50%) rotate(-45deg) skewX(2.33deg);
    border: none;
  }
`;

const SecContainerInner = styled.div`
  width: 1040px;
  margin: auto;
  background-color: var(--color-i-violet-dark-1);
  padding: ${Spacings.SPACING_6B};
  border: 1px solid #e9eef2;
  border-radius: ${Spacings.SPACING_2B};

  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: ${Spacings.SPACING_8B};
  }

  ${mediaQuery.uptoMobile} {
    padding: 0;
    border: none;
    border-radius: 0;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  ${mediaQuery.uptoMobile} {
    flex-direction: column-reverse;
  }
`;

const DetailsWrapper = styled.div``;

const HighLight = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_1B};
  align-items: center;
  margin-bottom: ${Spacings.SPACING_2B};
  .aquilla-typography {
    font-weight: ${Fonts.HEAVY};
    font-size: ${Fonts.H7};
  }
`;

const Header = styled.div`
  margin-bottom: ${Spacings.SPACING_3B};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
  }
`;

const Points = styled.div`
  position: relative;
  display: flex;
  gap: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_6B};
  flex-direction: row;

  ${mediaQuery.uptoTablet} {
    width: 100%;
    flex-direction: column;
    gap: ${Spacings.SPACING_2B};
  }
`;

const Point = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_5};
`;

const ImageWrapper = styled.div`
  width: 128px;
  aspect-ratio: 1/1;
  .free-trial {
    width: 100%;
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const StyledButton = styled(Button)`
  width: 240px;
  height: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: ${Spacings.SPACING_12B};
  }
`;

const ProofMarker = styled.div`
  position: absolute;
  width: ${Spacings.SPACING_4B};

  aspect-ratio: 1;
  border-radius: 50%;
  border-top: 1px solid #f4eee0;
  border-right: 1px solid #f4eee0;
  background-color: var(--color-base-light-0);

  :nth-of-type(1) {
    top: 20%;
    left: 0;
    transform: translateX(-50%) rotate(45deg);
  }
  :nth-of-type(2) {
    bottom: 20%;
    left: 0;
    transform: translateX(-50%) rotate(45deg);
  }
  :nth-of-type(3) {
    top: 20%;
    right: 0;
    transform: translateX(50%) rotate(225deg);
  }
  :nth-of-type(4) {
    bottom: 20%;
    right: 0;
    transform: translateX(50%) rotate(225deg);
  }
`;

const OfferValidationWrapper = styled.div`
  width: 240px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${Spacings.SPACING_1B};
  background-image: url('${getImage('home/freeTrial/freeTrialDays.svg')}');
  background-repeat: no-repeat;
  background-size: cover;
  height: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const FreeTrial = (props) => {
  const { freeTrialData, goalUID, onSubscribe, lps } = props;
  const dispatch = useDispatch();
  const { onClick } = useFreeTrial(goalUID, onSubscribe, dispatch);
  const isFreeUser = useMyInfo(getIsFreeUser(goalUID));
  const goalEventsProps = useGoalEventProps(goalUID);
  const { isOpen: showLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const experimentVariation = useExperiments(
    getExperimentVariation('seven_day_free_trial_web_experiment')
  );
  const isFreeTrialExperiment = !!(
    experimentVariation && experimentVariation === abVariations.TEST_1
  );

  useEffect(() => {
    if (!showLoginDrawer) segmentStorage.setItem('lpss', 'NA');
  }, [showLoginDrawer]);
  const handleActivate = () => {
    segmentStorage.setItem('lpss', 'Free Trial Section');
    segment.track('Free Trial - Activate Free Trial Clicked', {
      ...goalEventsProps,
      last_primary_source: lps,
      last_primary_source_section: 'Free Trial Section'
    });
    onClick();
  };
  if (!isFreeUser) return null;
  if (
    !isFreeTrialExperiment ||
    !freeTrialData ||
    !freeTrialData?.data?.daysRemaining
  )
    return null;
  return (
    <SecContainer>
      <Arrow />
      <SecContainerInner>
        <ContentWrapper>
          <DetailsWrapper>
            <HighLight>
              <Typography variant="p6" color="var(--color-i-orange)">
                FOR NEW MEMBERS
              </Typography>
              <StarIcon color="var(--color-i-orange)" />
            </HighLight>
            <Header>
              <Typography variant="h4">
                {freeTrialData?.data?.header}
              </Typography>
            </Header>
            <Points>
              <Point>
                <CheckContained width={20} height={20} />
                <Typography variant="p2">
                  Unlimited live classes from top educators
                </Typography>
              </Point>
              <Point>
                <CheckContained width={20} height={20} />
                <Typography variant="p2">
                  Unlimited tests, practice, notes & more
                </Typography>
              </Point>
            </Points>
            <ButtonWrapper>
              <StyledButton
                variant="brand"
                size="large"
                onClick={handleActivate}
              >
                Activate free trial
              </StyledButton>
              <OfferValidationWrapper>
                <ProofMarker />
                <ProofMarker />
                <ProofMarker />
                <ProofMarker />
                <Discount
                  width={20}
                  height={20}
                  color="var(--color-i-brown-13)"
                />
                <Typography variant="h5" color="var(--color-i-brown-13)">
                  Offer ends in {freeTrialData?.data?.daysRemaining} days
                </Typography>
              </OfferValidationWrapper>
            </ButtonWrapper>
          </DetailsWrapper>
          <ImageWrapper>
            <Image
              src="home/freeTrial/freeTrialBlock.svg"
              alt=""
              className="free-trial"
            />
          </ImageWrapper>
        </ContentWrapper>
      </SecContainerInner>
    </SecContainer>
  );
};

export default FreeTrial;
